import type { JournalAccount } from "@/apis/gql/types";
import { gql, useMutation } from "@apollo/client";
import { noop } from "lodash";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

type UpdateJournalAccount = {
  journalAccountIds: string[];
  number?: string | null;
  name?: string | null;
  accountType?: string | null;
  currency?: string | null;
  entityId?: string | null;
  entityType?: string | null;
};

const UPDATE_JOURNAL_ACCOUNTS_MUTATION = gql`
  mutation UpdateJournalAccounts(
    $journalAccountIds: [String]
    $updateFields: JournalAccountInput
  ) {
    updateJournalAccounts(
      journalAccountIds: $journalAccountIds
      updateFields: $updateFields
    ) {
      journalAccountId
      number
      name
      accountType
      currency
      entityId
      entityType
    }
  }
`;

export const useUpdateJournalAccounts = () => {
  const { t } = useTranslation();
  const [updateJournalAccounts, { data, loading, error }] = useMutation(
    UPDATE_JOURNAL_ACCOUNTS_MUTATION,
    {
      refetchQueries: ["GetAllJournalAccounts"],
    },
  );

  const handleUpdateJournalAccounts = async (
    data: UpdateJournalAccount,
    onSuccess: (data: JournalAccount[]) => void = noop,
    onError: () => void = noop,
  ) => {
    const { journalAccountIds, ...updateFields } = data;

    toast.dismiss();

    const toastId = toast.loading(t("generic_loading"));

    try {
      const response = await updateJournalAccounts({
        variables: {
          journalAccountIds,
          updateFields,
        },
      });

      if (response.errors) {
        throw new Error("error updating journal accounts");
      }

      onSuccess(response.data.updateJournalAccounts);

      toast.dismiss(toastId);

      return response;
    } catch (error) {
      onError();
      toast.error(t("generic_error"), {
        id: toastId,
        richColors: true,
      });
      throw error;
    }
  };

  return [handleUpdateJournalAccounts, { data, loading, error }] as const;
};
